/**
 * Automations Page
 * Rules list, create rule modal, and runs view
 * Phase 1 Consolidation: Global rule creation disabled - automations are now agent-scoped
 */
import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { useOrganization } from '../context/OrganizationContext';

import {
  CaretRight,
  Check,
  CheckCircle,
  CircleNotch,
  Clock,
  ClockCounterClockwise,
  Eye,
  GearSix,
  Lightning,
  ListBullets,
  Pause,
  PencilSimple,
  Play,
  Plus,
  Trash,
  Warning,
  X,
  XCircle
} from '@phosphor-icons/react';
const API_URL = BACKEND_URL;

// Action type labels
const ACTION_LABELS = {
  'salesforce.create_lead': 'Salesforce: Create Lead',
  'salesforce.create_task': 'Salesforce: Create Task',
  'salesforce.update_record': 'Salesforce: Update Record',
  'xero.create_invoice': 'Xero: Create Invoice',
  'xero.create_contact': 'Xero: Create Contact',
  'microsoft.send_email': 'Microsoft 365: Send Email',
  'microsoft.create_event': 'Microsoft 365: Create Event',
  'microsoft.upload_file': 'Microsoft 365: Upload File',
  'google.send_email': 'Google: Send Email',
  'google.upload_file': 'Google: Upload to Drive',
  'webhook.post': 'Webhook: POST Request'
};

// Available variables for mapping - organized by namespace with descriptions
const AVAILABLE_VARIABLES = [
  // Output namespace - Agent results
  { path: 'output.markdown', label: 'Agent Summary', category: 'output', 
    description: 'Full formatted output from the agent', bestFor: 'Email body, notes, long-form content' },
  { path: 'output.execution_status', label: 'Execution Status', category: 'output',
    description: 'Whether the agent succeeded or failed', bestFor: 'Conditions, subject lines, status updates' },
  { path: 'output.json_data', label: 'Structured Data', category: 'output',
    description: 'JSON object with structured fields', bestFor: 'CRM fields, structured records' },
  { path: 'output.json_data.status', label: 'Data: Status', category: 'output',
    description: 'Status field from structured output', bestFor: 'Record status, workflow state' },
  { path: 'output.json_data.name', label: 'Data: Name', category: 'output',
    description: 'Name field from structured output', bestFor: 'Contact names, lead names' },
  { path: 'output.json_data.email', label: 'Data: Email', category: 'output',
    description: 'Email field from structured output', bestFor: 'Email addresses, contact info' },
  { path: 'output.json_data.title', label: 'Data: Title', category: 'output',
    description: 'Title field from structured output', bestFor: 'Subject lines, record titles' },
  { path: 'output.sections', label: 'Output Sections', category: 'output',
    description: 'Array of content sections', bestFor: 'Multi-part content, reports' },
  // Session namespace - Context
  { path: 'session.id', label: 'Session ID', category: 'session',
    description: 'Unique identifier for this run', bestFor: 'Reference numbers, tracking' },
  { path: 'session.status', label: 'Session Status', category: 'session',
    description: 'Current session state', bestFor: 'Status tracking' },
  { path: 'session.created_at', label: 'Run Timestamp', category: 'session',
    description: 'When the session started', bestFor: 'Timestamps, audit logs' },
  { path: 'session.user_id', label: 'User ID', category: 'session',
    description: 'User who triggered the session', bestFor: 'Attribution, assignments' },
  // Agent namespace
  { path: 'agent.id', label: 'Agent ID', category: 'agent',
    description: 'Unique agent identifier', bestFor: 'Reference, logging' },
  { path: 'agent.name', label: 'Agent Name', category: 'agent',
    description: 'Name of the agent that ran', bestFor: 'Attribution, subject lines' },
  { path: 'agent.category', label: 'Agent Category', category: 'agent',
    description: 'Agent type/category', bestFor: 'Categorization, filtering' },
  // Organization namespace
  { path: 'org.id', label: 'Organization ID', category: 'org',
    description: 'Your organization identifier', bestFor: 'Multi-tenant operations' },
  { path: 'org.name', label: 'Organization Name', category: 'org',
    description: 'Your organization name', bestFor: 'Branding, signatures' },
  // Opportunity namespace (for opportunity.approved trigger)
  { path: 'opportunity.id', label: 'Opportunity ID', category: 'opportunity',
    description: 'Approved opportunity identifier', bestFor: 'CRM records, tracking' },
  { path: 'opportunity.title', label: 'Opportunity Title', category: 'opportunity',
    description: 'Name of the opportunity', bestFor: 'Subject lines, task names' },
  { path: 'opportunity.roi', label: 'Opportunity ROI', category: 'opportunity',
    description: 'Projected return on investment', bestFor: 'Financial fields, notes' },
  { path: 'opportunity.status', label: 'Opportunity Status', category: 'opportunity',
    description: 'Current opportunity state', bestFor: 'Status fields, conditions' },
  // System
  { path: 'now_date', label: 'Current Date/Time', category: 'system',
    description: 'Timestamp when rule executes', bestFor: 'Due dates, timestamps' }
];

// Suggested mappings based on action type - outcome-focused
const SUGGESTED_MAPPINGS = {
  'microsoft.send_email': [
    { label: 'Agent Summary → Email Body', targetField: 'body', sourceVar: '{{output.markdown}}', 
      description: 'Use the full agent output as the email content' },
    { label: 'Output Title → Subject Line', targetField: 'subject', sourceVar: '{{output.json_data.title}}',
      description: 'Use structured title as email subject' },
    { label: 'Session Reference → Footer', targetField: 'footer', sourceVar: 'Ref: {{session.id}}',
      description: 'Add tracking reference to email' }
  ],
  'microsoft.create_event': [
    { label: 'Output Title → Event Name', targetField: 'subject', sourceVar: '{{output.json_data.title}}',
      description: 'Use agent output as calendar event title' },
    { label: 'Agent Summary → Event Notes', targetField: 'body', sourceVar: '{{output.markdown}}',
      description: 'Add agent output as event description' }
  ],
  'salesforce.create_lead': [
    { label: 'Contact Name → Lead Name', targetField: 'LastName', sourceVar: '{{output.json_data.name}}',
      description: 'Map extracted name to Salesforce lead' },
    { label: 'Contact Email → Lead Email', targetField: 'Email', sourceVar: '{{output.json_data.email}}',
      description: 'Map extracted email to lead record' },
    { label: 'Agent Notes → Description', targetField: 'Description', sourceVar: '{{output.markdown}}',
      description: 'Add agent analysis as lead description' }
  ],
  'salesforce.create_task': [
    { label: 'Output Title → Task Subject', targetField: 'Subject', sourceVar: '{{output.json_data.title}}',
      description: 'Use output as task subject' },
    { label: 'Agent Summary → Task Notes', targetField: 'Description', sourceVar: '{{output.markdown}}',
      description: 'Add full agent output to task' }
  ],
  'xero.create_invoice': [
    { label: 'Contact Name → Invoice Contact', targetField: 'ContactName', sourceVar: '{{output.json_data.name}}',
      description: 'Map contact for invoice' },
    { label: 'Agent Notes → Reference', targetField: 'Reference', sourceVar: '{{output.json_data.title}}',
      description: 'Add reference to invoice' }
  ],
  'xero.create_contact': [
    { label: 'Contact Name → Name', targetField: 'Name', sourceVar: '{{output.json_data.name}}',
      description: 'Map extracted name to Xero contact' },
    { label: 'Contact Email → Email', targetField: 'EmailAddress', sourceVar: '{{output.json_data.email}}',
      description: 'Map extracted email to contact' }
  ],
  'google.send_email': [
    { label: 'Agent Summary → Email Body', targetField: 'body', sourceVar: '{{output.markdown}}',
      description: 'Use agent output as email content' },
    { label: 'Output Title → Subject', targetField: 'subject', sourceVar: '{{output.json_data.title}}',
      description: 'Use structured title as subject' }
  ],
  'webhook.post': [
    { label: 'Full Output → Payload', targetField: 'data', sourceVar: '{{output.json_data}}',
      description: 'Send structured data to webhook' },
    { label: 'Agent Summary → Message', targetField: 'message', sourceVar: '{{output.markdown}}',
      description: 'Include formatted output in payload' }
  ]
};

// Variable category descriptions for guided UI
const VARIABLE_CATEGORIES = {
  output: { 
    label: 'Agent Output', 
    description: 'Results from the AI agent execution',
    icon: '🤖'
  },
  session: { 
    label: 'Session Context', 
    description: 'Information about this specific run',
    icon: '📋'
  },
  agent: { 
    label: 'Agent Info', 
    description: 'Details about which agent ran',
    icon: '⚡'
  },
  org: { 
    label: 'Organization', 
    description: 'Your organization details',
    icon: '🏢'
  },
  opportunity: { 
    label: 'Opportunity', 
    description: 'Approved opportunity details',
    icon: '💡'
  },
  system: { 
    label: 'System', 
    description: 'System-generated values',
    icon: '⚙️'
  }
};

// Trigger type labels
const TRIGGER_LABELS = {
  'agent.completed': 'Agent Session Completed',
  'session.completed': 'Session Completed',
  'workflow.completed': 'Workflow Completed',
  'opportunity.approved': 'Opportunity Approved',
  'manual': 'Manual Trigger'
};

// Status badge component
function StatusBadge({ status }) {
  const config = {
    success: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
    failed: { color: 'bg-red-500/20 text-red-400', icon: XCircle },
    blocked: { color: 'bg-yellow-500/20 text-yellow-400', icon: Warning },
    running: { color: 'bg-blue-500/20 text-blue-400', icon: CircleNotch },
    pending: { color: 'bg-gray-500/20 text-gray-400', icon: Clock }
  };
  
  const { color, icon: Icon } = config[status] || config.pending;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${color}`}>
      <Icon className={`w-3 h-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export default function Automations() {
  const { activeOrganization } = useOrganization();
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rules');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [showAdvancedVariables, setShowAdvancedVariables] = useState(false);
  const [activeMappingIndex, setActiveMappingIndex] = useState(null);
  const { toast } = useToast();

  // Helper to get headers with organization context
  const getOrgHeaders = (contentType = true) => {
    const headers = {};
    if (contentType) {
      headers['Content-Type'] = 'application/json';
    }
    if (activeOrganization?.organization_id) {
      headers['X-Organization-Id'] = activeOrganization.organization_id;
    }
    return headers;
  };

  // New rule form state
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    trigger: 'agent.completed',
    action_type: 'webhook.post',
    connection_id: '',
    mappings: [],
    approval_required: true
  });

  // Get suggested mappings for current action type
  const currentSuggestions = SUGGESTED_MAPPINGS[newRule.action_type] || [];

  // Apply a suggested mapping
  const applySuggestedMapping = (suggestion) => {
    // Check if this mapping already exists
    const exists = newRule.mappings.some(
      m => m.target_field === suggestion.targetField && m.source_template === suggestion.sourceVar
    );
    
    if (exists) {
      toast({
        title: "Already added",
        description: `This mapping is already in your rule.`,
        variant: "default"
      });
      return;
    }

    setNewRule(prev => ({
      ...prev,
      mappings: [...prev.mappings, {
        target_field: suggestion.targetField,
        source_template: suggestion.sourceVar
      }]
    }));

    toast({
      title: "Mapping added",
      description: `${suggestion.label}`,
      className: "bg-[#1a1a1a] border-[#14F195]/30 text-white"
    });
  };

  // Insert variable at cursor or into active mapping
  const insertVariable = (varPath) => {
    const template = `{{${varPath}}}`;
    
    // Copy to clipboard as fallback
    navigator.clipboard.writeText(template);
    
    // If there's an active mapping, insert into the source field
    if (activeMappingIndex !== null && activeMappingIndex < newRule.mappings.length) {
      const currentValue = newRule.mappings[activeMappingIndex].source_template || '';
      setNewRule(prev => ({
        ...prev,
        mappings: prev.mappings.map((m, idx) => 
          idx === activeMappingIndex 
            ? { ...m, source_template: currentValue + template }
            : m
        )
      }));
      
      toast({
        title: "Variable inserted",
        description: `Added ${template} to mapping`,
        className: "bg-[#1a1a1a] border-[#14F195]/30 text-white"
      });
    } else {
      toast({
        title: "Copied to clipboard",
        description: template,
        className: "bg-[#1a1a1a] border-[#6366F1]/30 text-white"
      });
    }
  };

  useEffect(() => {
    if (activeOrganization?.organization_id) {
      fetchData();
    }
  }, [activeOrganization?.organization_id]);

  const fetchData = async () => {
    try {
      const headers = getOrgHeaders(false);
      const [rulesRes, runsRes, connectionsRes] = await Promise.all([
        fetch(`${API_URL}/api/org/automations/rules`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/org/automations/runs?limit=50`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/org/integrations`, { credentials: 'include', headers })
      ]);

      if (rulesRes.ok) {
        const data = await rulesRes.json();
        setRules(data.rules || []);
      }
      if (runsRes.ok) {
        const data = await runsRes.json();
        setRuns(data.runs || []);
      }
      if (connectionsRes.ok) {
        const data = await connectionsRes.json();
        setConnections(data.connections || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async () => {
    if (!newRule.name || !newRule.connection_id) {
      toast({
        title: 'Validation Error',
        description: 'Name and connection are required',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/org/automations/rules`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders(),
        body: JSON.stringify({
          name: newRule.name,
          description: newRule.description,
          trigger: newRule.trigger,
          conditions: [],
          actions: [{
            action_type: newRule.action_type,
            connection_id: newRule.connection_id,
            mappings: newRule.mappings
          }],
          approval_required: newRule.approval_required
        })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Automation rule created' });
        setCreateDialogOpen(false);
        setNewRule({
          name: '',
          description: '',
          trigger: 'agent.completed',
          action_type: 'webhook.post',
          connection_id: '',
          mappings: [],
          approval_required: true
        });
        setShowAdvancedVariables(false);
        setActiveMappingIndex(null);
        fetchData();
      } else {
        const data = await response.json();
        toast({
          title: 'Failed',
          description: data.detail || 'Failed to create rule',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create rule',
        variant: 'destructive'
      });
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      const response = await fetch(`${API_URL}/api/org/automations/rules/${rule.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: getOrgHeaders(),
        body: JSON.stringify({ is_active: !rule.is_active })
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update rule',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    try {
      const response = await fetch(`${API_URL}/api/org/automations/rules/${ruleId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getOrgHeaders(false)
      });

      if (response.ok) {
        toast({ title: 'Deleted', description: 'Rule deleted successfully' });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete rule',
        variant: 'destructive'
      });
    }
  };

  const handleRunRule = async (ruleId) => {
    try {
      const response = await fetch(`${API_URL}/api/org/automations/rules/${ruleId}/run`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders(),
        body: JSON.stringify({})
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: data.status === 'blocked' ? 'Awaiting Approval' : 'Running',
          description: data.status === 'blocked' 
            ? 'Run created and awaiting admin approval'
            : 'Automation started'
        });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to run automation',
        variant: 'destructive'
      });
    }
  };

  const handleApproveRun = async (runId) => {
    try {
      const response = await fetch(`${API_URL}/api/org/automations/runs/${runId}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders(false)
      });

      if (response.ok) {
        toast({ title: 'Approved', description: 'Run approved and executed' });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to approve run',
        variant: 'destructive'
      });
    }
  };

  const handleRejectRun = async (runId) => {
    try {
      const response = await fetch(`${API_URL}/api/org/automations/runs/${runId}/reject`, {
        method: 'POST',
        credentials: 'include',
        headers: getOrgHeaders(false)
      });

      if (response.ok) {
        toast({ title: 'Rejected', description: 'Run rejected' });
        fetchData();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reject run',
        variant: 'destructive'
      });
    }
  };

  const addMapping = () => {
    setNewRule(prev => ({
      ...prev,
      mappings: [...prev.mappings, { target_field: '', source_template: '' }]
    }));
  };

  const updateMapping = (index, field, value) => {
    setNewRule(prev => {
      const newMappings = [...prev.mappings];
      newMappings[index] = { ...newMappings[index], [field]: value };
      return { ...prev, mappings: newMappings };
    });
  };

  const removeMapping = (index) => {
    setNewRule(prev => ({
      ...prev,
      mappings: prev.mappings.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <CircleNotch className="w-8 h-8 animate-spin text-[#6366F1]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Automations & Integrations</h1>
            <p className="text-gray-400">
              View and manage automations triggered by your agents.
            </p>
          </div>
          {/* Phase 1 Consolidation: Global rule creation disabled - automations are now agent-scoped */}
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-amber-400">New automations are created from within agents</p>
            </div>
            <Button 
              onClick={() => navigate('/unified-agent-builder')}
              variant="outline"
              className="border-[#6366F1]/50 text-[#6366F1] hover:bg-[#6366F1]/10"
            >
              <Plus className="w-4 h-4 mr-2" />
              Open Agent Builder
            </Button>
          </div>
        </div>

        {/* Integrations Quick Access Card */}
        <Link to="/settings/automation-integrations">
          <Card className="bg-gradient-to-r from-[#6366F1]/10 to-[#EC4899]/10 border-[#6366F1]/30 hover:border-[#6366F1]/60 transition-all cursor-pointer mb-6 group">
            <CardContent className="flex items-center justify-between p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                  <GearSix className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white group-hover:text-[#6366F1] transition-colors">
                    Manage Integrations
                  </h3>
                  <p className="text-sm text-gray-400">
                    Connect Slack, Email, Calendars, CRMs and other tools to power your automations
                  </p>
                </div>
              </div>
              <CaretRight className="w-5 h-5 text-gray-400 group-hover:text-[#6366F1] group-hover:translate-x-1 transition-all" />
            </CardContent>
          </Card>
        </Link>

        {/* Navigation Tabs - Rules, Runs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className="bg-[#111111] border border-[rgba(255,255,255,0.1)]">
              <TabsTrigger value="rules" className="data-[state=active]:bg-[#6366F1]">
                <Lightning className="w-4 h-4 mr-2" />
                Rules ({rules.length})
              </TabsTrigger>
              <TabsTrigger value="runs" className="data-[state=active]:bg-[#6366F1]">
                <ClockCounterClockwise className="w-4 h-4 mr-2" />
                Runs ({runs.length})
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Rules Tab */}
          <TabsContent value="rules" className="space-y-4">
            {rules.length === 0 ? (
              <Card className="bg-[#111111] border-[rgba(255,255,255,0.1)]">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Lightning className="w-12 h-12 text-gray-600 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">No Automations Yet</h3>
                  <p className="text-gray-400 mb-2 text-center max-w-md">
                    Automations let agents take action when something happens — like a completed session or approved opportunity.
                  </p>
                  <p className="text-amber-400/80 text-sm mb-4 text-center max-w-md">
                    Automations are now created from within the Agent Builder's Config tab.
                  </p>
                  <Button 
                    onClick={() => navigate('/unified-agent-builder')}
                    className="bg-[#6366F1] hover:bg-[#5558E3]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Open Agent Builder
                  </Button>
                </CardContent>
              </Card>
            ) : (
              rules.map((rule) => (
                <Card key={rule.id} className="bg-[#111111] border-[rgba(255,255,255,0.1)]">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-white font-medium">{rule.name}</h3>
                          {!rule.is_active && (
                            <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 rounded text-xs">
                              Paused
                            </span>
                          )}
                          {rule.approval_required && (
                            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                              Requires Approval
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-400 mb-3">{rule.description || 'No description'}</p>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>Trigger: {rule.trigger}</span>
                          <span>Actions: {rule.actions?.length || 0}</span>
                          <span>Runs: {rule.run_count || 0}</span>
                          {rule.last_run_at && (
                            <span>Last run: {new Date(rule.last_run_at).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={rule.is_active}
                          onCheckedChange={() => handleToggleRule(rule)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRunRule(rule.id)}
                          disabled={!rule.is_active}
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRule(rule.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Runs Tab */}
          <TabsContent value="runs" className="space-y-4">
            {runs.length === 0 ? (
              <Card className="bg-[#111111] border-[rgba(255,255,255,0.1)]">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <ClockCounterClockwise className="w-12 h-12 text-gray-600 mb-4" />
                  <p className="text-gray-400">No automation runs yet</p>
                </CardContent>
              </Card>
            ) : (
              runs.map((run) => (
                <Card key={run.id} className="bg-[#111111] border-[rgba(255,255,255,0.1)]">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-white font-medium">{run.rule_name}</h3>
                          <StatusBadge status={run.status} />
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>Created: {new Date(run.created_at).toLocaleString()}</span>
                          {run.completed_at && (
                            <span>Completed: {new Date(run.completed_at).toLocaleString()}</span>
                          )}
                          {run.approved_by && (
                            <span>Approved by: {run.approved_by}</span>
                          )}
                        </div>

                        {run.error_message && (
                          <p className="text-sm text-red-400 mt-2">{run.error_message}</p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {run.status === 'blocked' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApproveRun(run.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRejectRun(run.id)}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedRun(run)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Rule Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => {
        setCreateDialogOpen(open);
        if (!open) {
          setShowAdvancedVariables(false);
          setActiveMappingIndex(null);
        }
      }}>
        <DialogContent className="bg-[#111111] border-[rgba(255,255,255,0.1)] max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-white text-lg">Create Automation Rule</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">
              When AI finishes thinking, turn the outcome into a business action.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-sm">Rule Name *</Label>
                <Input
                  value={newRule.name}
                  onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Create Lead on Qualification"
                  className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white h-9"
                />
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Description</Label>
                <Input
                  value={newRule.description}
                  onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What does this rule do?"
                  className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white h-9"
                />
              </div>
            </div>

            {/* Trigger */}
            <div>
              <Label className="text-gray-300 text-sm">Trigger Event</Label>
              <Select
                value={newRule.trigger}
                onValueChange={(value) => setNewRule(prev => ({ ...prev, trigger: value }))}
              >
                <SelectTrigger className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-[rgba(255,255,255,0.1)]">
                  {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-sm">Action Type</Label>
                <Select
                  value={newRule.action_type}
                  onValueChange={(value) => setNewRule(prev => ({ ...prev, action_type: value }))}
                >
                  <SelectTrigger className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[rgba(255,255,255,0.1)]">
                    {Object.entries(ACTION_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Connection *</Label>
                {(() => {
                  // Get provider from action type (e.g., 'microsoft.send_email' -> 'microsoft_365')
                  const actionProvider = newRule.action_type?.split('.')[0];
                  const providerMap = {
                    'microsoft': 'microsoft_365',
                    'google': 'google_workspace', 
                    'salesforce': 'salesforce',
                    'xero': 'xero',
                    'webhook': 'webhook'
                  };
                  const providerId = providerMap[actionProvider];
                  const isWebhook = actionProvider === 'webhook';
                  
                  // Filter connections for this provider
                  const relevantConnections = connections.filter(c => 
                    c.provider === providerId || (isWebhook && c.provider === 'webhook')
                  );
                  const connectedConnections = relevantConnections.filter(c => c.status === 'connected');
                  const hasConnections = connectedConnections.length > 0;

                  if (!hasConnections && !isWebhook) {
                    // No connections - show inline CTA
                    return (
                      <div className="mt-1 p-3 bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.3)] rounded-lg">
                        <p className="text-sm text-gray-300 mb-2">
                          No connections found for this action
                        </p>
                        <p className="text-xs text-gray-500 mb-3">
                          You need to configure and connect an integration before creating this rule.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setCreateDialogOpen(false);
                            // Navigate to integrations page
                            window.location.href = '/automations?tab=integrations';
                          }}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B] hover:bg-[#D97706] text-white text-sm font-medium rounded-md transition-colors"
                        >
                          <GearSix className="w-3 h-3" />
                          Configure & Connect Integration
                        </button>
                      </div>
                    );
                  }

                  return (
                    <Select
                      value={newRule.connection_id}
                      onValueChange={(value) => setNewRule(prev => ({ ...prev, connection_id: value }))}
                    >
                      <SelectTrigger className="bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white h-9">
                        <SelectValue placeholder="Select connection" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-[rgba(255,255,255,0.1)]">
                        {relevantConnections.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-400">
                            No connections for this provider
                          </div>
                        ) : (
                          relevantConnections.map((conn) => (
                            <SelectItem 
                              key={conn.id} 
                              value={conn.id}
                              disabled={conn.status !== 'connected'}
                            >
                              {conn.display_name}
                              {conn.status !== 'connected' && ' — Not connected'}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
            </div>

            {/* Guided Mapping Section */}
            {currentSuggestions.length > 0 && (
              <div className="p-3 bg-[rgba(20,241,149,0.05)] border border-[rgba(20,241,149,0.2)] rounded-lg">
                <Label className="text-gray-300 text-sm flex items-center gap-2 mb-2">
                  <Lightning className="w-4 h-4 text-[#14F195]" />
                  Suggested Mappings
                </Label>
                <p className="text-[11px] text-gray-500 mb-2">
                  Click to add recommended mappings for {ACTION_LABELS[newRule.action_type]?.split(':')[1] || 'this action'}
                </p>
                <div className="space-y-1.5">
                  {currentSuggestions.map((suggestion, idx) => {
                    const isApplied = newRule.mappings.some(
                      m => m.target_field === suggestion.targetField && m.source_template === suggestion.sourceVar
                    );
                    return (
                      <button
                        key={idx}
                        onClick={() => !isApplied && applySuggestedMapping(suggestion)}
                        disabled={isApplied}
                        className={`w-full text-left p-2 rounded-md transition-all ${
                          isApplied 
                            ? 'bg-[rgba(20,241,149,0.1)] border border-[#14F195]/40 cursor-default' 
                            : 'bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.08)] border border-transparent hover:border-[rgba(255,255,255,0.1)]'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-medium ${isApplied ? 'text-[#14F195]' : 'text-white'}`}>
                            {isApplied && <Check className="w-3 h-3 inline mr-1" />}
                            {suggestion.label}
                          </span>
                          {!isApplied && <Plus className="w-3 h-3 text-gray-500" />}
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5">{suggestion.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Field Mappings */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-gray-300 text-sm">Field Mappings</Label>
                <Button variant="ghost" size="sm" onClick={addMapping} className="h-7 text-xs">
                  <Plus className="w-3 h-3 mr-1" />
                  Add Custom
                </Button>
              </div>
              
              {newRule.mappings.length === 0 ? (
                <p className="text-xs text-gray-500 py-2">
                  {currentSuggestions.length > 0 
                    ? 'Use suggested mappings above or add custom mappings'
                    : 'Add mappings to define what data flows to your action'}
                </p>
              ) : (
                <div className="space-y-1.5 max-h-28 overflow-y-auto">
                  {newRule.mappings.map((mapping, idx) => (
                    <div 
                      key={idx} 
                      className={`flex gap-1.5 items-center p-1.5 rounded ${
                        activeMappingIndex === idx ? 'bg-[rgba(99,102,241,0.1)] ring-1 ring-[#6366F1]/50' : ''
                      }`}
                    >
                      <Input
                        value={mapping.target_field}
                        onChange={(e) => updateMapping(idx, 'target_field', e.target.value)}
                        placeholder="Target field"
                        className="flex-1 bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white text-xs h-8"
                      />
                      <span className="text-gray-500 text-xs">→</span>
                      <Input
                        value={mapping.source_template}
                        onChange={(e) => updateMapping(idx, 'source_template', e.target.value)}
                        onFocus={() => setActiveMappingIndex(idx)}
                        onBlur={() => setTimeout(() => setActiveMappingIndex(null), 200)}
                        placeholder="{{output.markdown}}"
                        className="flex-1 bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)] text-white text-xs h-8"
                      />
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeMapping(idx)}
                        className="text-red-400 h-7 w-7 p-0"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Variable Picker with Guided Descriptions */}
              <div className="mt-3 border-t border-[rgba(255,255,255,0.05)] pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400">
                    {showAdvancedVariables ? 'All Variables' : 'Available Data'} 
                    {activeMappingIndex !== null && <span className="text-[#6366F1] ml-1">(click to insert)</span>}
                  </span>
                  <button
                    onClick={() => setShowAdvancedVariables(!showAdvancedVariables)}
                    className="text-[10px] text-gray-500 hover:text-gray-300"
                  >
                    {showAdvancedVariables ? 'Show guided view' : 'Show raw variables'}
                  </button>
                </div>

                {showAdvancedVariables ? (
                  /* Advanced: Raw variable list (existing behavior) */
                  <div className="p-2 bg-[rgba(255,255,255,0.03)] rounded-lg max-h-36 overflow-y-auto">
                    {['output', 'session', 'agent', 'org', 'opportunity', 'system'].map(category => {
                      const vars = AVAILABLE_VARIABLES.filter(v => v.category === category);
                      if (vars.length === 0) return null;
                      if (category === 'opportunity' && newRule.trigger !== 'opportunity.approved') return null;
                      return (
                        <div key={category} className="mb-2">
                          <span className="text-[10px] text-gray-600 uppercase">{category}:</span>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {vars.map((v) => (
                              <span
                                key={v.path}
                                className="px-1.5 py-0.5 bg-[rgba(99,102,241,0.2)] text-[#6366F1] rounded text-[10px] cursor-pointer hover:bg-[rgba(99,102,241,0.3)]"
                                onClick={() => insertVariable(v.path)}
                                title={v.description || v.label}
                              >
                                {`{{${v.path}}}`}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Guided: Grouped variables with descriptions */
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {['output', 'session', 'opportunity'].map(category => {
                      const catInfo = VARIABLE_CATEGORIES[category];
                      const vars = AVAILABLE_VARIABLES.filter(v => v.category === category);
                      if (vars.length === 0) return null;
                      if (category === 'opportunity' && newRule.trigger !== 'opportunity.approved') return null;
                      
                      return (
                        <div key={category} className="p-2 bg-[rgba(255,255,255,0.02)] rounded-lg">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <span className="text-sm">{catInfo?.icon}</span>
                            <span className="text-xs font-medium text-gray-300">{catInfo?.label}</span>
                            <span className="text-[10px] text-gray-500">— {catInfo?.description}</span>
                          </div>
                          <div className="space-y-1">
                            {vars.slice(0, 4).map((v) => (
                              <button
                                key={v.path}
                                onClick={() => insertVariable(v.path)}
                                className="w-full text-left p-1.5 rounded hover:bg-[rgba(255,255,255,0.05)] transition-colors group"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-white">{v.label}</span>
                                  <code className="text-[9px] text-[#6366F1] opacity-0 group-hover:opacity-100 transition-opacity">
                                    {`{{${v.path}}}`}
                                  </code>
                                </div>
                                {v.bestFor && (
                                  <p className="text-[10px] text-gray-500">Best for: {v.bestFor}</p>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Approval Toggle */}
            <div className="flex items-center justify-between p-2 bg-[rgba(255,255,255,0.03)] rounded-lg">
              <div>
                <Label className="text-gray-300 text-sm">Require Approval</Label>
                <p className="text-[10px] text-gray-500">Admin must approve before execution</p>
              </div>
              <Switch
                checked={newRule.approval_required}
                onCheckedChange={(checked) => setNewRule(prev => ({ ...prev, approval_required: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setCreateDialogOpen(false)}
              className="border-[rgba(255,255,255,0.1)] text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateRule}
              className="bg-[#6366F1] hover:bg-[#5558E3]"
            >
              Create Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run Details Dialog */}
      <Dialog open={!!selectedRun} onOpenChange={() => setSelectedRun(null)}>
        <DialogContent className="bg-[#111111] border-[rgba(255,255,255,0.1)] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Run Details</DialogTitle>
          </DialogHeader>
          
          {selectedRun && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <StatusBadge status={selectedRun.status} />
                <span className="text-gray-400">{selectedRun.rule_name}</span>
              </div>

              {selectedRun.trigger_payload && (
                <div>
                  <Label className="text-gray-300 mb-2 block">Trigger Payload (Redacted)</Label>
                  <pre className="p-3 bg-[rgba(255,255,255,0.03)] rounded text-xs text-gray-400 overflow-auto max-h-40">
                    {JSON.stringify(selectedRun.trigger_payload, null, 2)}
                  </pre>
                </div>
              )}

              {selectedRun.action_results?.length > 0 && (
                <div>
                  <Label className="text-gray-300 mb-2 block">Action Results</Label>
                  {selectedRun.action_results.map((result, idx) => (
                    <div key={idx} className="p-3 bg-[rgba(255,255,255,0.03)] rounded mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white text-sm">{ACTION_LABELS[result.action_type] || result.action_type}</span>
                        <StatusBadge status={result.status} />
                      </div>
                      {result.error_message && (
                        <p className="text-sm text-red-400">{result.error_message}</p>
                      )}
                      {result.response_preview && (
                        <pre className="text-xs text-gray-500 mt-2 overflow-auto max-h-20">
                          {JSON.stringify(result.response_preview, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
